import { Component } from '@theme/component';

/**
 * Promo countdown modal. Reads its config from an embedded JSON script
 * (populated server-side by sections/counter-modal.liquid from the
 * `counter_modal` metaobject) and opens its parent `dialog-component`
 * according to the configured trigger, respecting a per-visitor frequency
 * cap stored in localStorage.
 *
 * @typedef {object} Config
 * @property {number} startTs - Unix seconds. 0 = no start gate.
 * @property {number} endTs - Unix seconds. The countdown target.
 * @property {'delay' | 'load' | 'scroll' | 'exit'} trigger
 * @property {number} delaySeconds
 * @property {number} scrollPercent
 * @property {number} frequencyDays - 0 = show every visit.
 * @property {boolean} hideOnCartAccount
 * @property {string} storageKey
 * @property {string} code - Discount code for the copy button ('' if none).
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} config
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 * @property {HTMLButtonElement} [copyButton]
 * @property {HTMLElement} [copyFeedback]
 *
 * @extends Component<Refs>
 */
class CounterModal extends Component {
  requiredRefs = ['config', 'days', 'hours', 'minutes', 'seconds'];

  /** @type {Config | null} */
  #config = null;
  /** @type {number | undefined} */
  #intervalId;
  /** @type {number | undefined} */
  #timeoutId;
  /** @type {boolean} */
  #opened = false;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#config = JSON.parse(this.refs.config.textContent || '{}');
    } catch {
      return;
    }
    if (!this.#config || !this.#config.endTs) return;

    // El contador se rellena aunque el modal aún no se haya abierto.
    this.#tick();
    this.#intervalId = window.setInterval(() => this.#tick(), 1000);

    if (this.refs.copyButton) {
      this.refs.copyButton.addEventListener('click', this.#copyCode);
    }

    const cta = this.querySelector('.counter-modal__cta');
    if (cta) cta.addEventListener('click', () => this.#markSeen());

    const dc = this.closest('dialog-component');
    if (dc) dc.addEventListener('dialog:close', () => this.#markSeen());

    if (this.#shouldShow()) this.#scheduleTrigger();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#intervalId);
    window.clearTimeout(this.#timeoutId);
    window.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('mouseout', this.#onMouseOut);
  }

  /** @returns {boolean} */
  #shouldShow() {
    const c = this.#config;
    if (!c) return false;

    const nowSec = Math.floor(Date.now() / 1000);
    if (c.startTs > 0 && nowSec < c.startTs) return false;
    if (nowSec >= c.endTs) return false;

    if (c.hideOnCartAccount) {
      const p = window.location.pathname;
      if (p === '/cart' || p.startsWith('/cart/') || p.startsWith('/account')) return false;
    }

    if (c.frequencyDays > 0) {
      let last = 0;
      try {
        last = Number(window.localStorage.getItem(c.storageKey) || 0);
      } catch {
        last = 0;
      }
      if (last && Date.now() - last < c.frequencyDays * 86400000) return false;
    }

    return true;
  }

  #scheduleTrigger() {
    const c = this.#config;
    if (!c) return;

    switch (c.trigger) {
      case 'load':
        this.#open();
        break;
      case 'scroll':
        window.addEventListener('scroll', this.#onScroll, { passive: true });
        this.#onScroll();
        break;
      case 'exit':
        document.addEventListener('mouseout', this.#onMouseOut);
        break;
      case 'delay':
      default:
        this.#timeoutId = window.setTimeout(() => this.#open(), Math.max(0, c.delaySeconds) * 1000);
        break;
    }
  }

  #onScroll = () => {
    const c = this.#config;
    if (!c) return;
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const pct = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
    if (pct >= c.scrollPercent) this.#open();
  };

  /** @param {MouseEvent} event */
  #onMouseOut = (event) => {
    if (!event.relatedTarget && event.clientY <= 0) this.#open();
  };

  #open() {
    if (this.#opened) return;
    this.#opened = true;
    window.clearTimeout(this.#timeoutId);
    window.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('mouseout', this.#onMouseOut);

    const dc = /** @type {{ showDialog?: () => void } | null} */ (this.closest('dialog-component'));
    dc?.showDialog?.();
  }

  #markSeen() {
    const c = this.#config;
    if (!c || c.frequencyDays <= 0) return;
    try {
      window.localStorage.setItem(c.storageKey, String(Date.now()));
    } catch {
      /* modo incógnito / storage bloqueado: no pasa nada */
    }
  }

  #tick() {
    const c = this.#config;
    if (!c) return;

    const remaining = c.endTs - Math.floor(Date.now() / 1000);
    if (remaining <= 0) {
      this.#render(0, 0, 0, 0);
      window.clearInterval(this.#intervalId);
      const dc = /** @type {{ closeDialog?: () => void } | null} */ (this.closest('dialog-component'));
      dc?.closeDialog?.();
      return;
    }

    this.#render(
      Math.floor(remaining / 86400),
      Math.floor((remaining % 86400) / 3600),
      Math.floor((remaining % 3600) / 60),
      remaining % 60
    );
  }

  /**
   * @param {number} d
   * @param {number} h
   * @param {number} m
   * @param {number} s
   */
  #render(d, h, m, s) {
    this.refs.days.textContent = this.#pad(d);
    this.refs.hours.textContent = this.#pad(h);
    this.refs.minutes.textContent = this.#pad(m);
    this.refs.seconds.textContent = this.#pad(s);
  }

  /** @param {number} value @returns {string} */
  #pad(value) {
    return String(Math.max(0, value)).padStart(2, '0');
  }

  #copyCode = async () => {
    const c = this.#config;
    if (!c || !c.code) return;

    let done = false;
    try {
      await navigator.clipboard.writeText(c.code);
      done = true;
    } catch {
      const ta = document.createElement('textarea');
      ta.value = c.code;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try {
        done = document.execCommand('copy');
      } catch {
        done = false;
      }
      document.body.removeChild(ta);
    }

    const feedback = this.refs.copyFeedback;
    if (done && feedback) {
      feedback.textContent = feedback.dataset.copied || '';
      feedback.classList.remove('visually-hidden');
      window.setTimeout(() => {
        feedback.textContent = '';
        feedback.classList.add('visually-hidden');
      }, 2000);
    }
  };
}

if (!customElements.get('counter-modal')) {
  customElements.define('counter-modal', CounterModal);
}
